import { Image } from '@rocco/components/image';
import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import type { ReactNode } from 'react';
import {
    BLEED_GUTTER_X,
    OVERLAY_CAP,
    useIslandCarousel,
} from '~/ui-rocco/pages/detail-shared';

export interface FeaturedCarouselSlide {
    id: number;
    title: string;
    link: string;
    imageSrc?: string;
    /** Drives the filled Watch Video control. */
    hasVideo?: boolean;
}

interface FeaturedFilmCarouselProps {
    slides: FeaturedCarouselSlide[];
    className?: string;
}

/**
 * Registers the carousel with the floating pagination island, as the product,
 * project and professional browse pages do, rather than drawing a pill of its
 * own beneath the slide.
 *
 * A component rather than a hook call in the parent because `useIslandCarousel`
 * reads the enclosing `<Carousel>`'s embla state, which only resolves inside it.
 * The ref wants an element spanning the carousel, so it wraps the track.
 */
const FeaturedCarouselIsland = ({ children }: { children: ReactNode }) => {
    const ref = useIslandCarousel<HTMLDivElement>({
        label: 'Featured articles',
    });
    return <div ref={ref}>{children}</div>;
};

/**
 * The featured article/film carousel on /articles.
 *
 * Figma 60:2064: a 1920x953 frame carrying an 1824-wide slide centred on a
 * 48px margin, with the neighbouring slides bleeding through the gutter — the
 * cue that the row continues. Slide content sits at 54.3 from the slide's own
 * left edge, not the page's.
 */
export const FeaturedFilmCarousel = ({
    slides,
    className,
}: FeaturedFilmCarouselProps) => {
    if (slides.length === 0) return null;

    return (
        <section
            aria-label="Featured articles"
            className={cn('w-full', className)}
        >
            <Carousel
                options={{ align: 'start', loop: slides.length > 2 }}
                ariaLabel="Featured films"
            >
                {/*
                 * The 48px gutter goes on the viewport, not the track: embla
                 * measures the viewport, so snaps land on the gutter while
                 * overflow clips at the padding box — which is what lets the
                 * neighbouring slides stay visible through it. A slide is then
                 * the viewport's content width, 1824 at 1920.
                 */}
                <FeaturedCarouselIsland>
                    <CarouselContent
                        viewportClassName={BLEED_GUTTER_X}
                        className="ml-0 gap-[1.125rem]"
                    >
                        {slides.map((slide) => (
                            <CarouselItem
                                key={slide.id}
                                className="basis-full pl-0"
                            >
                                <div className="relative aspect-[1824/953] w-full overflow-hidden bg-[#131313]">
                                    {slide.imageSrc && (
                                        <Image
                                            src={slide.imageSrc}
                                            alt=""
                                            fill={true}
                                            fit="cover"
                                            className="absolute inset-0"
                                        />
                                    )}

                                    {/* Same reasoning as the masthead: the design's
                                    slide photo is dark behind the headline and
                                    real ones are not. */}
                                    <div
                                        aria-hidden={true}
                                        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#131313]/[0.72] via-[#131313]/[0.30] via-[55%] to-transparent to-[85%]"
                                    />

                                    <div className={cn(OVERLAY_CAP, 'px-0')}>
                                        {/*
                                        Three quarters of the frame's 89.5/97
                                        and anchored by its own bottom edge.
                                        The design's mock title is short enough
                                        to set at full size in two lines; real
                                        article titles run two to three times
                                        that, so at the drawn size a headline
                                        breaks off mid-word. Growing upward from
                                        a fixed baseline holds the gap to the
                                        controls whatever the title runs to.
                                    */}
                                        <h3
                                            /*
                                             * The padding is descender room: the
                                             * clamp clips at the box edge, and at
                                             * this leading Gestura's g and y fall
                                             * below the last line box. The negative
                                             * margin puts the box back where the
                                             * bottom anchor had it.
                                             */
                                            className="absolute m-0 -mb-[0.2em] line-clamp-3 pb-[0.2em] font-gestura text-[4.1972rem] leading-[4.5469rem] font-normal tracking-[-0.1679rem] text-[#e5e5e5]"
                                            style={{
                                                left: '2.977%',
                                                bottom: '31.826%',
                                                width: '48.836%',
                                            }}
                                        >
                                            {slide.title}
                                        </h3>

                                        <div
                                            className="absolute flex items-center gap-[1.723rem]"
                                            style={{
                                                left: '2.977%',
                                                top: '74.66%',
                                            }}
                                        >
                                            {slide.hasVideo && (
                                                <RoccoLink
                                                    to={slide.link}
                                                    className="flex h-[3.25rem] items-center justify-center rounded-full bg-white px-[1.723rem] font-sans text-[1rem] leading-[1.3] font-medium text-black drop-shadow-[0_0.2027rem_0.7094rem_rgba(0,0,0,0.11)]"
                                                >
                                                    Watch Video
                                                </RoccoLink>
                                            )}
                                            <RoccoLink
                                                to={slide.link}
                                                className="flex h-[3.25rem] items-center justify-center rounded-full border border-solid border-white px-[1.723rem] font-sans text-[1rem] leading-[1.3] font-medium text-[#f3f3f3] shadow-[0_0.2027rem_1.4189rem_rgba(0,0,0,0.11)] transition-colors hover:bg-white/10"
                                            >
                                                Read Article
                                            </RoccoLink>
                                        </div>
                                    </div>
                                </div>
                            </CarouselItem>
                        ))}
                    </CarouselContent>
                </FeaturedCarouselIsland>
            </Carousel>
        </section>
    );
};
